import { defineMessages } from "react-intl"
import { BulkActionDefinition, BulkActionType, ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { Permissions } from "../../../../role/Guard"
import { BULK_OPERATION_MESSAGES, BULK_OPERATION_TYPE, isInvoiceBulkExportSupported } from "../../../../utils/automation/bulk"
import { BILLING_TYPE } from "../../../invoice/InvoiceBillingType"
import { PREFERENCE_TYPE } from "../../constants"

export enum InvoiceColumnIds {
    IDENTIFIER = "identifier",
    LOCATION_NAME = "locationName",
    PROJECT_TITLE = "projectTitle",
    CONTACT = "contact",
    EFFECTIVE_DATE = "effectiveDate",
    DUE_DATE = "dueDate",
    STATUS = "status",
    TOTAL_AMOUNT = "totalAmount",
    BALANCE = "balance",
    ARCHIVED = "isArchived",
    PO_NUMBER = "poNumber",
    CUSTOMER_TYPE = "customerType",
    LAST_PAYMENT_DATE = "lastPaymentAt",
    FULLY_PAID_DATE = "fullyPaidAt",
    CREATED_AT_DATE = "createdAt",
    UPDATED_AT_DATE = "updatedAt",
    IMPORTED_AT_DATE = "importedAt",
    VOIDED_AT_DATE = "voidedAt",
    CONTACT_NAME = "contactName",
    CONTACT_FIRST_NAME = "contactFirstName",
    CONTACT_LAST_NAME = "contactLastName",
    CONTACT_ORGANIZATION_NAME = "contactOrganizationName",
    CONTACT_TYPE = "contactType",
    CONTACT_PHONE_NUMBER = "contactPhoneNumber",
    CONTACT_TAX_NUMBER = "contactTaxNumber",
    CONTACT_EMAIL = "contactEmail",
    PAID_AMOUNT = "paidAmount",
    CURRENCY = "currency",
    PAYMENT_TRANSACTIONS_TYPE = "paymentTransactionsType",
    PAYMENT_TRANSACTIONS_AMOUNT = "paymentTransactionsAmount",
    PRODUCTS_TITLE = "productsTitle",
    PRODUCTS_SKU = "productsSku",
    PRODUCTS_LABELS = "productsLabels",
    PAYMENT_TERM_ID = "paymentTermId",
    TAX_ID = "taxId",
    TAX_AMOUNT = "totalTaxAmount"
}

export enum InvoiceStatBox {
    OVERDUE = "overdue",
    OPEN = "open",
    FULLY_PAID_30_DAYS = "fully-paid-30-days",
    DRAFT = "draft",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: InvoiceColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/invoices/{id}/edit",
            secondaryValueKey: InvoiceColumnIds.PO_NUMBER,
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: InvoiceColumnIds.LOCATION_NAME,
        sortId: "locationNameSort",
        dataType: DataType.LOCATION,
        dataAttributes: {
            redirect: "/locations/{locationId}/edit"
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id:  InvoiceColumnIds.PROJECT_TITLE,
        sortId: "projectTitleSort",
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: InvoiceColumnIds.CONTACT,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.EFFECTIVE_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.DUE_DATE,
        dataType: DataType.DATE,
        dataAttributes: {
            dateExpiryStatuses: ["draft", "partially-paid", "sent", "active", "inactive", "finished"]
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                "draft": {
                    message: {
                        id: "generic-listing.invoices.badge.draft",
                        defaultMessage: "Draft",
                    },
                    type: "primary",
                },
                "sent": {
                    message: {
                        id: "generic-listing.invoices.badge.sent",
                        defaultMessage: "Waiting for payment",
                    },
                    type: "info",
                },
                "partially-paid": {
                    message: {
                        id: "generic-listing.invoices.badge.partially-paid",
                        defaultMessage: "Partially paid",
                    },
                    type: "warning",
                },
                "fully-paid": {
                    message: {
                        id: "generic-listing.invoices.badge.fully-paid",
                        defaultMessage: "Fully paid",
                    },
                    type: "success",
                },
                "voided": {
                    message: {
                        id: "generic-listing.invoices.badge.voided",
                        defaultMessage: "Voided",
                    },
                    type: "danger",
                },
                "available": {
                    message: {
                        id: "generic-listing.invoices.badge.available",
                        defaultMessage: "Available",
                    },
                    type: "primary",
                },
                "partially-applied": {
                    message: {
                        id: "generic-listing.invoices.badge.partially-applied",
                        defaultMessage: "Partially applied",
                    },
                    type: "warning",
                },
                "fully-applied": {
                    message: {
                        id: "generic-listing.invoices.badge.fully-applied",
                        defaultMessage: "Fully applied",
                    },
                    type: "success",
                },
                "pending": {
                    message: {
                        id: "generic-listing.invoices.badge.pending",
                        defaultMessage: "Pending",
                    },
                    type: "info",
                },
                "cancelled": {
                    message: {
                        id: "generic-listing.invoices.badge.canceled",
                        defaultMessage: "Canceled",
                    },
                    type: "danger",
                },
            },
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: InvoiceColumnIds.TOTAL_AMOUNT,
        accessorFn: row => row.totalAmount ?? row.baseCurrencyTotalAmount,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyTotalAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: InvoiceColumnIds.BALANCE,
        accessorFn: row => row.balance ?? row.baseCurrencyBalance,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyBalance",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: InvoiceColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: InvoiceColumnIds.PO_NUMBER,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: InvoiceColumnIds.CUSTOMER_TYPE,
        sortId: "contact.customerTypeName",
        accessorFn: row => row.contact?.customerTypeName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.LAST_PAYMENT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.FULLY_PAID_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.IMPORTED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.VOIDED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_NAME,
        sortId: "contact.nameSort",
        accessorFn: row => row.contact?.name,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_FIRST_NAME,
        sortId: "contact.firstNameKeyword",
        accessorFn: row => row.contact?.firstName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_LAST_NAME,
        sortId: "contact.lastNameKeyword",
        accessorFn: row => row.contact?.lastName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_ORGANIZATION_NAME,
        sortId: "contact.organizationNameSort",
        accessorFn: row => row.contact?.organizationName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_TYPE,
        accessorFn: row => row.contact?.type,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "persons": {
                    id: "generic-listing.contact-type.people",
                    defaultMessage: "People",
                },
                "organizations": {
                    id: "generic-listing.contact-type.organization",
                    defaultMessage: "Organization",
                },
                "suppliers": {
                    id: "generic-listing.contact-type.supplier",
                    defaultMessage: "Supplier",
                },
            }
        },
        sortable: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_PHONE_NUMBER,
        sortId: "contact.phoneNumberKeyword",
        accessorFn: row => row.contact?.phoneNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_TAX_NUMBER,
        sortId: "contact.taxNumber",
        accessorFn: row => row.contact?.taxNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.CONTACT_EMAIL,
        sortId: "contact.emailKeyword",
        accessorFn: row => row.contact?.email,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InvoiceColumnIds.PAID_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: InvoiceColumnIds.CURRENCY,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: InvoiceColumnIds.TAX_AMOUNT,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: InvoiceColumnIds.PAYMENT_TRANSACTIONS_TYPE,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.paymentType",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "paymentTypeName",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: InvoiceColumnIds.PAYMENT_TRANSACTIONS_AMOUNT,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.amount",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "amount",
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: InvoiceColumnIds.PRODUCTS_TITLE,
        accessorKey: "rows",
        sortId: "rowsObject.title",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "title",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: InvoiceColumnIds.PRODUCTS_SKU,
        accessorKey: "rows",
        sortId: "rowsObject.sku",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "sku",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: InvoiceColumnIds.PRODUCTS_LABELS,
        accessorKey: "rows",
        sortId: "rowsObject.labelIds",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "labelIds",
            optionsKey: "labelOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: InvoiceColumnIds.PAYMENT_TERM_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "paymentTermOptions",
            loadingKey: "loadingPaymentTermOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: InvoiceColumnIds.TAX_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
]

const headerMessages = defineMessages({
    [InvoiceColumnIds.IDENTIFIER]: {
        id: "generic-listing.invoices.table-header.identifier",
        defaultMessage: "ID",
    },
    [InvoiceColumnIds.LOCATION_NAME]: {
        id: "generic-listing.invoices.table-header.locationName",
        defaultMessage: "Location",
    },
    [InvoiceColumnIds.PROJECT_TITLE]: {
        id: "generic-listing.invoices.table-header.projectTitle",
        defaultMessage: "Title",
    },
    [InvoiceColumnIds.CONTACT]: {
        id: "generic-listing.invoices.table-header.contact",
        defaultMessage: "Contact",
    },
    [InvoiceColumnIds.EFFECTIVE_DATE]: {
        id: "generic-listing.invoices.table-header.effectiveDate",
        defaultMessage: "Sent date",
    },
    [InvoiceColumnIds.DUE_DATE]: {
        id: "generic-listing.invoices.table-header.dueDate",
        defaultMessage: "Due date",
    },
    [InvoiceColumnIds.STATUS]: {
        id: "generic-listing.invoices.table-header.status",
        defaultMessage: "Status",
    },
    [InvoiceColumnIds.TOTAL_AMOUNT]: {
        id: "generic-listing.invoices.table-header.totalAmount",
        defaultMessage: "Amount",
    },
    [InvoiceColumnIds.BALANCE]: {
        id: "generic-listing.invoices.table-header.balance",
        defaultMessage: "Balance",
    },
    [InvoiceColumnIds.ARCHIVED]: {
        id: "generic-listing.invoices.table-header.archived",
        defaultMessage: "Archived",
    },
    [InvoiceColumnIds.PO_NUMBER]: {
        id: "generic-listing.invoices.table-header.po-number",
        defaultMessage: "PO Number",
    },
    [InvoiceColumnIds.CUSTOMER_TYPE]: {
        id: "generic-listing.invoices.table-header.customer-type",
        defaultMessage: "Customer Type",
    },
    [InvoiceColumnIds.LAST_PAYMENT_DATE]: {
        id: "generic-listing.invoices.table-header.last-payment-date",
        defaultMessage: "Last Payment Date",
    },
    [InvoiceColumnIds.FULLY_PAID_DATE]: {
        id: "generic-listing.invoices.table-header.full-payment-date",
        defaultMessage: "Fully Paid Date",
    },
    [InvoiceColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.invoices.table-header.created-at-date",
        defaultMessage: "Created At Date",
    },
    [InvoiceColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.invoices.table-header.update-at-date",
        defaultMessage: "Updated At Date",
    },
    [InvoiceColumnIds.IMPORTED_AT_DATE]: {
        id: "generic-listing.invoices.table-header.imported-at-date",
        defaultMessage: "Imported At Date",
    },
    [InvoiceColumnIds.VOIDED_AT_DATE]: {
        id: "generic-listing.invoices.table-header.voided-at-date",
        defaultMessage: "Voided At Date",
    },
    [InvoiceColumnIds.CONTACT_NAME]: {
        id: "generic-listing.invoices.table-header.contact-name",
        defaultMessage: "Contact Name",
    },
    [InvoiceColumnIds.CONTACT_FIRST_NAME]: {
        id: "generic-listing.invoices.table-header.contact-first-name",
        defaultMessage: "Contact First Name",
    },
    [InvoiceColumnIds.CONTACT_LAST_NAME]: {
        id: "generic-listing.invoices.table-header.contact-last-name",
        defaultMessage: "Contact Last Name",
    },
    [InvoiceColumnIds.CONTACT_ORGANIZATION_NAME]: {
        id: "generic-listing.invoices.table-header.contact-organization-name",
        defaultMessage: "Contact Organization",
    },
    [InvoiceColumnIds.CONTACT_TYPE]: {
        id: "generic-listing.invoices.table-header.contact-type",
        defaultMessage: "Contact Type",
    },
    [InvoiceColumnIds.CONTACT_PHONE_NUMBER]: {
        id: "generic-listing.invoices.table-header.contact-phone_number",
        defaultMessage: "Contact Phone Number",
    },
    [InvoiceColumnIds.CONTACT_TAX_NUMBER]: {
        id: "generic-listing.invoices.table-header.contact-tax-number",
        defaultMessage: "Contact Tax Number",
    },
    [InvoiceColumnIds.CONTACT_EMAIL]: {
        id: "generic-listing.invoices.table-header.contact-email",
        defaultMessage: "Contact Email",
    },
    [InvoiceColumnIds.PAID_AMOUNT]: {
        id: "generic-listing.invoices.table-header.paid-amount",
        defaultMessage: "Paid Amount",
    },
    [InvoiceColumnIds.CURRENCY]: {
        id: "generic-listing.invoices.table-header.currency",
        defaultMessage: "Currency",
    },
    [InvoiceColumnIds.TAX_AMOUNT]: {
        id: "generic-listing.invoices.table-header.total-tax-amount",
        defaultMessage: "Tax Amount",
    },
    [InvoiceColumnIds.PAYMENT_TRANSACTIONS_TYPE]: {
        id: "generic-listing.invoices.table-header.payment-transactions-type",
        defaultMessage: "Payment Transactions Type",
    },
    [InvoiceColumnIds.PAYMENT_TRANSACTIONS_AMOUNT]: {
        id: "generic-listing.invoices.table-header.payment-transactions-amount",
        defaultMessage: "Payment Transactions Amount",
    },
    [InvoiceColumnIds.PRODUCTS_TITLE]: {
        id: "generic-listing.invoices.table-header.product-title",
        defaultMessage: "Products Title",
    },
    [InvoiceColumnIds.PRODUCTS_SKU]: {
        id: "generic-listing.invoices.table-header.product-sku",
        defaultMessage: "Products SKU",
    },
    [InvoiceColumnIds.PRODUCTS_LABELS]: {
        id: "generic-listing.invoices.table-header.product-labels",
        defaultMessage: "Products Labels",
    },
    [InvoiceColumnIds.PAYMENT_TERM_ID]: {
        id: "generic-listing.invoices.table-header.payment-term-id",
        defaultMessage: "Payment Term",
    },
    [InvoiceColumnIds.TAX_ID]: {
        id: "generic-listing.invoices.table-header.tax-id",
        defaultMessage: "Tax",
    },
})

const bulkActionDefinitions: BulkActionDefinition[] = [
    {
        id: BULK_OPERATION_TYPE.APPLY_INVOICE_PAYMENT,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.APPLY_INVOICE_PAYMENT],
        icon: "dollar-sign",
    },
    {
        id: BULK_OPERATION_TYPE.SEND_EMAIL,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.SEND_EMAIL],
        icon: "envelope",
    },
    {
        id: BULK_OPERATION_TYPE.PRINT_INVOICE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.PRINT_INVOICE],
        icon: "print",
    },
    {
        id: BULK_OPERATION_TYPE.EDIT_INVOICE_LOCATION,
        message: {
            id: "generic-listing.bulk-actions.edit-invoices-location",
            defaultMessage: "Edit invoices location",
        },
        icon: "map-marker-alt",
    },
    {
        id: BULK_OPERATION_TYPE.EXPORT,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.EXPORT],
        icon: "print",
        permission: isInvoiceBulkExportSupported(),
    },
    {
        id: BULK_OPERATION_TYPE.DELETE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.DELETE],
        icon: "trash",
        type: BulkActionType.DANGER,
        guard: Permissions.DeleteInvoice,
    },
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.INVOICES,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_INVOICES,
    searchPlaceholder: {
        id: "generic-listing.invoices.search-placeholder",
        defaultMessage: "Search an invoice...",
    },
    displayString: {
        id: "generic-listing.invoices.display-string",
        defaultMessage: "Invoices",
    },
    searchObjectType: ObjectType.INVOICES,
    additionalFilters: {
        billingType: [BILLING_TYPE.ONETIME],
    },
    bulkActionDefinitions,
    statBoxComponent: require("../../../../common/listing/generic-listing/stat-boxes/InvoicesStatBoxes"),
    exportModalComponent: require("../../../../common/listing/generic-listing/export-modals/InvoiceExportModal"),
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/InvoiceCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/invoices.ts